import { Link } from 'react-router-dom';
import { Activity, Droplets } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import type { EquipmentRuntime, RelayRun } from '../../types/equipment';
import { formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import { toNum } from '@/utils/numeric';

interface PumpRuntimeCardProps {
  /** GET /equipment/:id/runtime — null while loading or after a failure. */
  runtime: EquipmentRuntime | null;
  failed: boolean;
  /** Whether this is a pump — an unlinked pump gets told how to link it. */
  isPump: boolean;
}

function runLength(run: RelayRun): string {
  if (!run.ended_at) return 'running';
  const mins = Math.round((new Date(run.ended_at).getTime() - new Date(run.started_at).getTime()) / 60000);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/**
 * Where a pump's hours come from.
 *
 * A pump linked to an irrigation node gets its hours from that node's relay:
 * every period the relay reports on is added to the hour meter when it ends,
 * and the service intervals on this page count against that meter. This card
 * says which relay, whether it is running now, and the recent runs — so a
 * reading that looks wrong can be traced back to the runs behind it.
 *
 * A run cut short at the credit cap is marked: that is a lost "off" report,
 * not a pump that ran for a day.
 */
export default function PumpRuntimeCard({ runtime, failed, isPump }: PumpRuntimeCardProps) {
  const orgTz = useOrgTimezone();

  if (!runtime) {
    // Only a pump has a reason to show anything while the answer is unknown.
    if (!isPump || !failed) return null;
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-neutral-500">
          Could not read this pump&apos;s irrigation link.
        </CardContent>
      </Card>
    );
  }

  const { link, running_since, runs } = runtime;
  if (!link && !isPump) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Droplets className="h-4 w-4 text-sky-600" />
          Run time
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3 text-sm">
        {!link ? (
          <p className="text-neutral-600">
            Not linked to an irrigation relay, so its hours only change when somebody types a meter reading.
            To count them automatically, open the pump on the{' '}
            <Link to="/irrigation/network" className="text-sky-700 underline">irrigation network map</Link>{' '}
            and choose this machine under <span className="font-medium">Equipment record</span>.
          </p>
        ) : (
          <>
            <p className="text-neutral-600">
              Hours are counted from the relay on{' '}
              <span className="font-medium text-neutral-800 break-words">{link.node_name ?? 'its node'}</span>
              {' '}at {link.farm_name}
              {link.relay_router_mac == null && (
                <span className="text-amber-700"> — but that node has no relay chosen, so nothing is being counted yet</span>
              )}
              .
            </p>
            {running_since && (
              <p className="flex items-center gap-1.5 text-sky-700 font-medium">
                <Activity className="h-4 w-4 animate-pulse" />
                Running since {formatDateTimeInTz(running_since, orgTz)}
              </p>
            )}
            {runs.length > 0 ? (
              <ul className="divide-y divide-neutral-100">
                {runs.slice(0, 6).map(run => {
                  const credited = toNum(run.credited_hours);
                  return (
                    <li key={run.id} className="py-1.5 flex flex-wrap justify-between gap-x-3 gap-y-0.5">
                      <span className="text-neutral-700">{formatDateTimeInTz(run.started_at, orgTz)}</span>
                      <span className="text-neutral-500">
                        {runLength(run)}
                        {credited != null && ` · +${credited.toFixed(2)} h`}
                        {run.capped && (
                          <span className="text-amber-700" title="The relay's off report never arrived; only the cap was counted.">
                            {' '}(capped)
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-neutral-500">No runs recorded yet.</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
